import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import type { RequestError } from '../../shared';
import type { Account } from '../model';

/**
 * Default options for account-related queries
 */
export const DEFAULT_ACCOUNT_QUERY_OPTIONS = {
  staleTime: 10_000,
  gcTime: 2 * 60_000,
} as const;

/**
 * Wrapper for account-dependent queries with sensible defaults
 */
export function useAccountQuery<TData = unknown, TError = RequestError>(
  queryKey: readonly unknown[],
  queryFn: ({ signal }: { signal?: AbortSignal }) => Promise<TData>,
  account: Account | null | undefined,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn' | 'enabled'> & {
    enabled?: boolean;
  },
): UseQueryResult<TData, TError> {
  return useQuery({
    queryKey,
    queryFn,
    enabled: Boolean(account) && (options?.enabled ?? true),
    ...DEFAULT_ACCOUNT_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * Wrapper for queries that depend on multiple conditions (e.g., account + another condition)
 */
export function useConditionalQuery<TData = unknown, TError = RequestError>(
  queryKey: readonly unknown[],
  queryFn: ({ signal }: { signal?: AbortSignal }) => Promise<TData>,
  enabled: boolean,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn' | 'enabled'>,
): UseQueryResult<TData, TError> {
  return useQuery({
    queryKey,
    queryFn,
    enabled,
    ...DEFAULT_ACCOUNT_QUERY_OPTIONS,
    ...options,
  });
}
